import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Waves, Flame, Heart } from 'lucide-react';

const FeatureCard = ({ icon: Icon, title, description, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-50px' }}
    transition={{ duration: 0.6, delay }}
    className="group relative p-8 rounded-2xl bg-white border border-[#E2E8F0] hover:border-[#0D9488]/30 hover:shadow-xl hover:shadow-[#0D9488]/5 transition-all duration-500"
  >
    <div className="w-14 h-14 rounded-2xl bg-[#0D9488]/8 flex items-center justify-center mb-6 group-hover:bg-[#0D9488]/15 transition-colors duration-500">
      <Icon className="w-7 h-7 text-[#0D9488]" />
    </div>
    <h3 className="font-display text-xl font-bold text-[#0C4A6E] mb-3">{title}</h3>
    <p className="text-[#64748B] leading-relaxed">{description}</p>
  </motion.div>
);

export const About = () => {
  const { t } = useLanguage();

  return (
    <section id="about" className="relative py-28 lg:py-36 bg-[#FAFAF8] overflow-hidden">
      {/* Subtle texture */}
      <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #0C4A6E 1px, transparent 0)', backgroundSize: '40px 40px' }} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-sm font-semibold text-[#0D9488] tracking-[0.2em] uppercase mb-4">
              {t('about.tagline')}
            </span>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#0C4A6E] mb-6">
              {t('about.title')}
            </h2>
          </motion.div>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="/assets/images/about/about-main.png"
                alt="Fresh Sinaloa style ceviche and mariscos at Cristian's Baja Salada Los Cabos"
                className="w-full h-[480px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0C4A6E]/20 to-transparent" />
            </div>
            {/* Floating accent */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 rounded-2xl bg-[#0D9488]/10 -z-10" />
            <div className="absolute -top-6 -left-6 w-24 h-24 rounded-2xl bg-[#F0FDFA] -z-10" />
          </motion.div>

          {/* Text */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2 className="sr-only">Seafood in Cabo San Lucas - Fresh Sinaloa Style Mariscos</h2>
            <p className="text-lg text-[#475569] leading-relaxed mb-6">
              {t('about.description')}
            </p>
            <p className="text-lg text-[#475569] leading-relaxed mb-6">
              {t('about.description2')}
            </p>
            {/* SEO Content */}
            <div className="space-y-4 mb-6">
              <h3 className="text-xl font-semibold text-[#0C4A6E]">{t('about.seoHeading1')}</h3>
              <p className="text-[#475569] leading-relaxed">
                {t('about.seoContent1')}
              </p>
              <p className="text-[#475569] leading-relaxed">
                {t('about.seoContent2')}
              </p>
            </div>
            <div className="mt-8 flex items-center gap-6">
              <div className="flex -space-x-3">
                {['SM', 'CR', 'JT', 'MG'].map((initials, i) => (
                  <div
                    key={i}
                    className="w-10 h-10 rounded-full bg-[#0D9488] flex items-center justify-center text-white text-xs font-bold border-2 border-white"
                  >
                    {initials}
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 mb-0.5">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-amber-400 fill-current" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <span className="text-sm text-[#64748B]">4.9/5 · 300+ reviews</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Feature Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          <FeatureCard
            icon={Waves}
            title={t('about.quality')}
            description={t('about.qualityDesc')}
            delay={0}
          />
          <FeatureCard
            icon={Flame}
            title={t('about.tradition')}
            description={t('about.traditionDesc')}
            delay={0.1}
          />
          <FeatureCard
            icon={Heart}
            title={t('about.family')}
            description={t('about.familyDesc')}
            delay={0.2}
          />
        </div>
      </div>
    </section>
  );
};
